import { Component, AfterViewInit, OnDestroy, ViewChild } from '@angular/core';
import { Config } from '@config';
import { UpgradeService } from './upgrade.service';
import { SiteFile } from './upgrade.info.interface';
import { RoutingInfoService } from '@routing';
import { TenantService } from '@tenant';
import { __i18n } from '@localisation';
import { UrlEnhancerService } from '@url';
import { UpgradeProcess } from './upgrade.process';
import { Comparator, copy } from '@tools';
import { Subject } from 'rxjs';
import { ClxButtonComponent, ButtonStates } from '@forms/clx-forms/controls/clx.button.component';
import { DialogService, DialogType } from '@ui-dialog';

@Component({
    selector: 'upgrade',
    templateUrl: './upgrade.template.html',
    standalone: false
})
export class UpgradeComponent implements AfterViewInit, OnDestroy {
    public upgradeResource: any;
    public actions: UpgradeProcess[];

    constructor(
        public upgradeService: UpgradeService,
        private config: Config,
        private routingInfoService: RoutingInfoService,
        private tenantService: TenantService,
        private urlEnhancer: UrlEnhancerService,
        private comparator: Comparator,
        private dialogService: DialogService
    ){
        const dataObservable = this.dataSubject.asObservable();
        this.upgradeResource = {
            model$ : dataObservable
        }
    }

    public dataSubject :  Subject<any> = new Subject();

    @ViewChild('parameter', { static : false})
    public parameter: any;

    @ViewChild('bacnetParameter', { static : false})
    public bacnetParameter: any;

    @ViewChild('acknowledgeButton', { static : false})
    public acknowledgeButton: ClxButtonComponent;

    public useApplicationSetText: string = __i18n('use application set', 'upgrade.user-application-set.text');

    public chooseFileText: string = __i18n('choose file', 'upgrade.choose-file.text');

    public upgradeButtonState: boolean = null;

    private subscriptions: any[] = [];

    public getVersionCellStyle(siteFile: SiteFile){
        if(siteFile.associatedFile){
            if(siteFile.uuidDevice) {
                if(
                    (siteFile.associatedFile.fileUuid === siteFile.uuidDevice)
                    || (
                        siteFile.associatedFile.fileUuid === this.config.emptyGuid
                        && (this.hasEmptyGuid(siteFile.uuidDevice))
                    )
                    ){
                        return 'success-background inverted-font-color';
                }else{
                    return 'error-background inverted-font-color';
                }
            }else{
                return 'grey-background inverted-font-color';
            }
        }else{
            return 'success-background inverted-font-color';
        }
    }

    public getLocalCellStyle(siteFile: SiteFile){
        if(siteFile.associatedFile){
            if(siteFile.uuidFile) {
                if(
                    (siteFile.associatedFile.fileUuid === siteFile.uuidFile)
                    || (
                        siteFile.associatedFile.fileUuid === this.config.emptyGuid
                        && (this.hasEmptyGuid(siteFile.uuidFile))
                    )
                ){
                    return 'success-background inverted-font-color';
                }
                return 'error-background inverted-font-color';
            }else{
                if(
                    (siteFile.uuidDevice === siteFile.associatedFile.fileUuid
                    || (
                        siteFile.associatedFile.fileUuid === this.config.emptyGuid
                        && this.hasEmptyGuid(siteFile.uuidDevice))
                    )
                ){
                    return 'dark-yellow-background inverted-font-color';
                }
                return 'error-background inverted-font-color';
            }
        }else{
            return 'success-background inverted-font-color';
        }
    }

    public hasEmptyGuid(value: string){
        if(
            value
            && (
                value === 'ffffffff-ffff-ffff-ffff-ffffffffffff'
                || value === this.config.emptyGuid
            )
        ){
            return true;
        }
        return false;
    }

    public currentApplicationSet: string;

    public oldData : any = null;

    public async ngAfterViewInit() {
        const plantId = this.routingInfoService.getCurrentRoutingParams().plantid;
        const tenantId = this.tenantService.getCurrentTenantId();
        const observable = await this.upgradeService.startStream();
        this.subscriptions.push(
            observable.subscribe((data) => {
                if(this.changesPredicted(this.oldData, data)){
                    this.updateData(data);
                    this.dataSubject.next(data);
                    this.oldData = copy(data);
                }
            })
        )

        this.actions =  this.upgradeService.getActions();
        const status = await this.upgradeService.isPlantOnline();
        if(status){
            this.upgradeButtonState = null;
        }
    }

    public updateData(current){
        if(current && current.ctrlTraceFiles && current.ctrlTraceFiles.length) {
            current.traceSelect = current.ctrlTraceFiles.map((current) => {
                return {
                    text : current.FileName,
                    value : current.SiteFileId
                };
            });
            if(!current.traceSelect.find((current) => current.selected)){
                current.traceSelect[0].selected = true;
            }
        }
        if(current && current.applicationSetId !== this.currentApplicationSet){
            this.upgradeService.clearSendFileCache();
            this.currentApplicationSet = current.applicationSetId;
        }
    }


    public changesPredicted(oldModel, newModel){
        if(newModel && newModel.ctrlTraceFiles && newModel.ctrlTraceFiles.length) {
            newModel.traceSelect = newModel.ctrlTraceFiles.map((current) => {
                return {
                    text : current.FileName,
                    value : current.SiteFileId
                };
            });
        }
        return !this.comparator.areEqual(oldModel, newModel, ['selected']);
    }


    public convertParameterValue(value: any){
        return value.map((current) => {
            return {
                text : current.FileName, value : current.SiteFileId
            };
        });
    }

    public getTestName(siteFile) {
        let name = siteFile.deviceFriendlyName || siteFile.deviceName;
        name = name.replace(/ /gm, '-');
        return name.toLowerCase();
    }

    public download(file: string){
        switch(file) {
            case 'parameter':
                file = (this.parameter.selectedEntry)
                    ? this.parameter.selectedEntry.SiteFileId || this.parameter.selectedEntry.id : '';
                break;
            case 'bacnetParameter':
                file = (this.bacnetParameter.selectedEntry)
                    ? this.bacnetParameter.selectedEntry.SiteFileId || this.bacnetParameter.selectedEntry.id : '';
                break;
            case 'releaseNotes':
                file = 'releaseNotesFile'
                break;

        }
        if(file) {
            const url = this.urlEnhancer.replaceParams(this.config.services.upgrade.downloadFile)
                + '?filename=' + encodeURI(file);

            const element = document.createElement('a');
            element.setAttribute('href', url);
            element.setAttribute('download', file);
            element.style.display = 'none';
            document.body.appendChild(element);
            element.click();
            document.body.removeChild(element);
        }else{
            console.info('File not defined!')
        }
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((current) => {
            if(current && current.unsubscribe){
                current.unsubscribe();
            }
        });
        this.upgradeService.reset();
        this.oldData = null;
    }

    public downloadFile(siteFile: SiteFile){
        if(siteFile && siteFile.associatedFile && siteFile.associatedFile.fileUuid){
            this.download(siteFile.associatedFile.id);
        }
    }

    public getStateText(commandResult: any, commandId: number){
        switch(commandResult.status){
            case 0:
                return __i18n('OK', 'upgrade.command.result.status.0');
            case 4:
                return commandResult.message;
            case 5:
                return __i18n('in progress...', 'upgrade.command.result.status.5');
            case 6:
                return __i18n('Timeout!', 'upgrade.command.result.status.6');
            case 999:
                return ''
        }
        return commandResult.message;
    }

    public resetStateTexts(){
        this.actions.forEach((current) => {
            if(current.subject){
                if(current.subject.getValue().status === 0){
                    current.subject.next({
                        message: '',
                        payload: null,
                        status: 999
                    });
                }
            }
        });
    }

    public fileStates: any = {};

    public getFileState(deviceFriendlyName: string){
        return this.fileStates[deviceFriendlyName];
    }


    public realeasNotesStatus: string = 'ok';

    public async acknowledgeReleasNotes() {

        const result = await this.dialogService.show({
            type: DialogType.yesNo,
            title: __i18n('Acknowlege notification', 'operating.upgrade.dialog.title.acknowlege-notificatio'),
            message: __i18n('You really want to acknowlege the notification?', 'operating.upgrade.dialog.text.acknowlege-notification')
        });

        if(result) {
            this.acknowledgeButton.state = ButtonStates.loading;
            await this.upgradeService.acknowlegeUpgradPriority()
        }else{
            this.acknowledgeButton.state = null;
        }
    }
}
